/* LAV TDMS — TRM card and MDR card (slide-over drawers). */
import type { CSSProperties, ReactNode } from 'react'
import type { Doc, Mdr, ReviewCode, Transmittal, Vdoc, Vdr } from '@/data/types'
import type { Tone } from '@/ui'
import { Icon } from '@/ds/icons'
import {
  Button,
  Badge,
  Avatar,
  Purpose,
  CodeChip,
  LifeBadge,
  DiscTag,
  Crit,
  Field,
  Drawer,
  KPIStat,
  CompletionBar,
  clickable,
} from '@/ui'
import { useCoding, useDictLabels, useDictRows, useMe } from '@/data/meta'
import { STATUS_LABEL } from '@/screens/pt/Registers'
import { request, saveFile } from '@/data/api'
import { exportTrmForm } from '@/lib/excel'
import { downloadTrmPackage } from '@/lib/package'
import { SheetPrint } from '@/screens/pt/CrsSheet'
import { CodeLink } from '@/screens/pt/DocumentCard'
import { uploadTrmFile } from '@/screens/pt/TrmFiles'
import { fileSize } from '@/screens/pt/Viewer'
import { qk, useItem, useList, useSub, inboxKeys } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import { useRef, useState } from 'react'
import { Dialog, DateInput, DictPicker, Input, Select, toast } from '@/ui'
import { revVer } from '@/lib/format'
import { t, type Lang } from '@/i18n/lang'


 

/**
 * Правка письма.
 *
 * <p>Собранное письмо не правилось ничем: опечатка в теме или в номере
 * контрагента означала «аннулировать и выпустить заново», а до отправки
 * править нечего было и подавно. Сервер принимал PATCH с V47, а формы для
 * него не существовало.
 *
 * <p>Получатель и цель выпуска меняются только у черновика: номер выделен под
 * получателя, а по отправленному письму подписан приём.
 */
function LetterDialog({ open, code, draft, lang, initial, onClose }: {
  open: boolean
  code: string
  draft: boolean
  lang: Lang
  initial: { subject: string; recipient: string; due: string | null; issuedOn: string | null
             fao: string | null; cc: string | null; authority: string | null; remarks: string | null; partnerNo: string | null }
  onClose: () => void
}) {
  const [form, setForm] = useState({
    subject: initial.subject ?? '',
    recipient: initial.recipient ?? '',
    due: initial.due ?? '',
    issuedOn: initial.issuedOn ?? '',
    fao: initial.fao ?? '',
    cc: initial.cc ?? '',
    authority: initial.authority ?? '',
    remarks: initial.remarks ?? '',
    partnerNo: initial.partnerNo ?? '',
  })
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch })
  const save = useCommand<typeof form>({
    method: 'PATCH',
    path: () => `/transmittals/${encodeURIComponent(code)}`,
    body: (a) => a,
    invalidate: () => [qk.all('transmittals'), qk.item('transmittals', code),
                       qk.sub(`/transmittals/${encodeURIComponent(code)}/letter`), qk.all('audit')],
  })
  return (
    <Dialog open={open} onClose={onClose} width={620} title={`${t(lang, 'Правка письма', 'Edit the letter')} · ${code}`}>
      <div style={{ width: 560, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {draft
            ? t(lang, 'Черновик правится целиком: получатель и реквизиты бланка. Цель выпуска у письма своя не задаётся — она у документов состава.',
                      'A draft is fully editable: recipient and blank details. A letter has no purpose of its own — the documents carry it.')
            : t(lang, 'Отправленное письмо правится в реквизитах: получатель закреплён номером.',
                      'A sent letter keeps its recipient: it is fixed by its number.')}
        </div>
        <Input label={t(lang, 'Тема письма', 'Subject')} value={form.subject} onChange={(e) => set({ subject: e.target.value })} />
        {draft ? (
          <Input label={t(lang, 'Получатель', 'Recipient')} value={form.recipient} onChange={(e) => set({ recipient: e.target.value })} />
        ) : null}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={t(lang, 'Дата письма', 'Letter date')} value={form.issuedOn} onChange={(e) => set({ issuedOn: e.target.value })} placeholder="ДД.ММ.ГГГГ" />
          <Input label={t(lang, 'Срок ответа', 'Response due')} value={form.due} onChange={(e) => set({ due: e.target.value })} placeholder="ДД.ММ.ГГГГ" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={t(lang, 'Кому лично (FAO)', 'For attention of')} value={form.fao} onChange={(e) => set({ fao: e.target.value })} />
          <Input label={t(lang, 'Копии (CC)', 'CC')} value={form.cc} onChange={(e) => set({ cc: e.target.value })} />
          <Input label={t(lang, 'Полномочия', 'Authority')} value={form.authority} onChange={(e) => set({ authority: e.target.value })} />
          <Input label={t(lang, '№ контрагента', 'Partner ref')} value={form.partnerNo} onChange={(e) => set({ partnerNo: e.target.value })} />
        </div>
        <Input label={t(lang, 'Отметки в письме', 'Remarks')} value={form.remarks} onChange={(e) => set({ remarks: e.target.value })} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={save.isPending}
                  onClick={() => save.mutate({
                    ...form,
                    /* Пустую тему не отправляем: сервер понял бы её как «стереть».
                       Получателя и цель — только у черновика: у отправленного
                       письма сервер их и не примет. */
                    subject: form.subject.trim() === '' ? initial.subject : form.subject,
                    recipient: draft ? form.recipient : '',
                  }, {
                    onSuccess: () => { onClose(); toast({ tone: 'success', title: t(lang, 'Письмо изменено', 'Letter updated'), text: code }) },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не сохранилось', 'Not saved'), text: e.message }),
                  })}>{save.isPending ? t(lang, 'Сохраняем…', 'Saving…') : t(lang, 'Сохранить', 'Save')}</Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Дописать документы в состав черновика.
 *
 * <p>Забытый документ означал «удалить черновик и набрать состав заново».
 * Предлагаются документы того же подрядчика, которым письмо адресовано;
 * границу реестра и наличие файла держит сервер — теми же правилами, какими
 * отбивает выпуск.
 */
function AddItemsDialog({ open, code, vendor, present, lang, onClose }: {
  open: boolean
  code: string
  vendor: string
  present: string[]
  lang: Lang
  onClose: () => void
}) {
  const [filter, setFilter] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const docsQuery = useList<Doc>(
    'docs',
    { page: 0, pageSize: 200, sort: [{ id: 'no', desc: false }], filters: { vendor: [vendor] } },
    { enabled: open && vendor !== '' },
  )
  const rows = (docsQuery.data?.rows ?? []).filter((d) => !present.includes(d.no))
  const needle = filter.trim().toLowerCase()
  const shown = needle ? rows.filter((d) => (d.no + ' ' + d.title).toLowerCase().includes(needle)) : rows
  const toggle = (no: string) => { const n = new Set(picked); if (n.has(no)) n.delete(no); else n.add(no); setPicked(n) }
  const add = useCommand<{ docNos: string[] }>({
    path: () => `/transmittals/${encodeURIComponent(code)}/items`,
    body: (a) => a,
    invalidate: () => [qk.sub(`/transmittals/${encodeURIComponent(code)}/items`), qk.all('transmittals'), qk.all('audit')],
  })
  return (
    <Dialog open={open} onClose={onClose} width={640} title={`${t(lang, 'Дописать документы', 'Add documents')} · ${code}`}>
      <div style={{ width: 580, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Пакет живёт в границах одного реестра, и передавать нечего без файла: чужие строки сервер отобьёт.',
                   'A package stays within one register and needs a file to transfer: the server refuses the rest.')}
        </div>
        <Input size="sm" value={filter} onChange={(e) => setFilter(e.target.value)}
               leading={<Icon name="search" size={13} />} placeholder={t(lang, 'Поиск по документу…', 'Search documents…')} />
        <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
          {shown.map((d) => (
            <label key={d.no} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
              <input type="checkbox" checked={picked.has(d.no)} onChange={() => toggle(d.no)} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)', width: 170 }}>{d.no}</span>
              <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>{d.rev}</span>
            </label>
          ))}
          {shown.length === 0 ? (
            <div style={{ padding: '14px 12px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {docsQuery.isPending ? t(lang, 'Загрузка…', 'Loading…') : t(lang, 'Свободных документов нет', 'No documents available')}
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={picked.size === 0 || add.isPending}
                  title={picked.size ? undefined : t(lang, 'Ничего не выбрано', 'Nothing picked')}
                  onClick={() => add.mutate({ docNos: [...picked] }, {
                    onSuccess: () => { setPicked(new Set()); onClose(); toast({ tone: 'success', title: t(lang, 'Состав дополнен', 'Composition extended'), text: code }) },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не добавлено', 'Not added'), text: e.message }),
                  })}>{add.isPending ? t(lang, 'Добавляем…', 'Adding…') : t(lang, 'Добавить', 'Add')} · {picked.size}</Button>
        </div>
      </div>
    </Dialog>
  )
}

/* Носитель позиции по-человечески: в описи его читает человек. */
const MEDIUM_NAME = (medium: 'el' | 'paper' | 'both', lang: Lang) =>
  medium === 'paper' ? t(lang, 'Бумага', 'Paper')
    : medium === 'both' ? t(lang, 'Бумага и эл.', 'Paper & el.')
    : t(lang, 'Электронно', 'Electronic')

const TRM_STATUS: Record<string, Tone> = {
  Зарегистрирован: 'info',
  /* Приёмка и отправка по матрице — разные события: «Проверен» означает
     пройденный входной контроль, «Принят» — назначенный маршрут. Прежде
     приёмка сразу ставила «На рассмотрении», хотя рассматривать было некому. */
  Проверен: 'info',
  Принят: 'accent',
  'На рассмотрении': 'accent',
  Замечания: 'warning',
  Отправлен: 'info',
  Закрыт: 'success',
  Отклонён: 'danger',
}

function DocRow({ d, onOpenDoc }: { d: Doc | Vdoc; lang: Lang; onOpenDoc?: (no: string) => void }) {
  return (
    <button onClick={() => onOpenDoc && onOpenDoc(d.no)} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 14px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Crit on={('crit' in d && d.crit)} /><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{d.no}</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>рев. {d.rev}</span></div>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</div>
      </div>
      <Purpose value={d.purpose} size="sm" />
      <CodeChip code={d.code} size="sm" />
      <LifeBadge life={d.life} />
      <span style={{ color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name="chevron-right" size={15} /></span>
    </button>)

}

function CardHead({ children }: { children: ReactNode }) {
  return (
    /* Своей кнопки «Закрыть» здесь нет: она стоит в шапке самой выдвижной
       панели, и вторая давала два «Закрыть» в одном role=dialog. */
    <div style={{ flex: '0 0 auto', borderBottom: '1px solid var(--border-default)', padding: '16px 20px' }}>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>)

}
function Panel({ title, count, action, children }: { title: ReactNode; count?: number | null; action?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <h3 style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-muted)' }}>{title}</h3>
        {count != null ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 2, padding: '0 5px' }}>{count}</span> : null}
        <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
        {action}
      </div>
      {children}
    </section>)

}

const note = (danger?: boolean): CSSProperties => ({ padding: '16px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: danger ? 'var(--danger-ink)' : 'var(--text-subtle)' })

/* Пока запись не пришла с сервера, карточка показывает состояние, а не данные
   из памяти вкладки: экран не рисует того, чего нет в базе. */
/**
 * Тело панели, пока карточка не пришла.
 *
 * <p>Именно тело, а не отдельная панель: своя панель на время ожидания — это
 * другой тип компонента в той же позиции, React выбрасывает её и монтирует
 * заново, когда приходят данные, и въезд проигрывается второй раз. Со стороны
 * это выглядит как «карточка открылась дважды».
 */
function CardState({ lang, error }: { lang: Lang; error: boolean }) {
  return (
    <div style={note(error)}>
      {error ? t(lang, 'Не удалось загрузить карточку', 'Failed to load the card') : t(lang, 'Загрузка…', 'Loading…')}
    </div>
  )
}

/* Список документов карточки. Загрузка и отказ показываются явно: пустой
   список «ещё не пришло» и пустой список «документов нет» — разные вещи. */
function DocList({ pending, error, rows, lang, onOpenDoc, empty }: { pending: boolean; error: boolean; rows: (Doc | Vdoc)[]; lang: Lang; onOpenDoc?: (no: string) => void; empty?: ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--surface-card)' }}>
      {pending ? <div style={note()}>{t(lang, 'Загрузка…', 'Loading…')}</div> :
        error ? <div style={note(true)}>{t(lang, 'Не удалось загрузить документы', 'Failed to load documents')}</div> :
          rows.length ? rows.map((d) => <DocRow key={d.no} d={d} lang={lang} onOpenDoc={onOpenDoc} />) : empty}
    </div>)

}

/* Ряд плана и прогноза S-кривой в системе пока никто не считает: прежний график
   рисовался по выдуманным константам (planEnd=100, forecastEnd=96) поверх
   периодов из seed. Вместо правдоподобной картинки — честная заглушка. */
function CurvePlaceholder({ lang }: { lang: Lang }) {
  return (
    <div style={{ padding: '14px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
      {t(lang, 'S-кривая появится вместе с агрегатами дашборда проекта.', 'The S-curve lands together with the project dashboard aggregates.')}
    </div>)

}

interface TrmReceipt {
  files: { kind: string; name: string; size: number; uploadedBy: string | null; uploadedAt: string | null; url: string }[]
  dir?: 'in' | 'out'
  status?: string
  acknowledgedAt: string | null
  acknowledgedBy: string | null
}

const TRM_FILE_KIND: Record<string, [string, string]> = {
  cover: ['Сопроводительное письмо', 'Cover letter'],
  /* Снимок отправленного (V69): бланк собирается из полей, а поля правятся и
     после отправки — значит через полгода печатался бы не тот бланк. */
  issued: ['Экземпляр отправленного', 'Sent copy'],
  signed: ['Подписанный экземпляр', 'Signed copy'],
  other: ['Прочее', 'Other'],
}

/**
 * Передача: чем она сопровождается и состоялась ли.
 *
 * <p>До этого у трансмиттала не было ни одного своего файла — сопроводительное
 * письмо и подписанный скан прикладывать было некуда, а «Отправлен» означало
 * «выпущен», а не «принят другой стороной».
 *
 * <p>Подтверждает получатель, и кнопку видит он: у отправителя её нет вовсе —
 * подтверждение самому себе не доказательство передачи.
 */
function TrmHandover({ code, lang }: { code: string; lang: Lang }) {
  const receipt = useSub<TrmReceipt>(`/transmittals/${encodeURIComponent(code)}/receipt`)
  const me = useMe().data
  const fileRef = useRef<HTMLInputElement>(null)
  const [kind, setKind] = useState<'cover' | 'signed' | 'other'>('cover')
  const [busy, setBusy] = useState(false)

  const ack = useCommand<Record<string, never>>({
    path: () => `/transmittals/${encodeURIComponent(code)}/acknowledge`,
    body: () => ({}),
    invalidate: () => [qk.sub(`/transmittals/${encodeURIComponent(code)}/receipt`), qk.all('transmittals'), qk.all('audit')],
  })
  /* Отметка получения за подрядчика: пакет передали курьером или на совещании,
     подрядчик расписался на бумаге. Без неё трансмиттал висит «Отправлен», а
     срок рассмотрения считается не от той даты. */
  const offline = useCommand<{ date: string; by: string }>({
    path: () => `/transmittals/${encodeURIComponent(code)}/acknowledge-offline`,
    body: (a) => a,
    invalidate: () => [qk.sub(`/transmittals/${encodeURIComponent(code)}/receipt`), qk.all('transmittals'), qk.all('audit')],
  })
  const [offlineOpen, setOfflineOpen] = useState(false)
  const [receiptDate, setReceiptDate] = useState('')
  const [receiptBy, setReceiptBy] = useState('')

  const data = receipt.data
  const isVendor = Boolean(me?.user.vendor)
  /* Получатель исходящего — подрядчик, входящего — сторона заказчика. Тот же
     разбор делает сервер; здесь он решает только, показывать ли кнопку. */
  const iAmRecipient = data?.dir === 'out' ? isVendor : !isVendor
  const acknowledged = Boolean(data?.acknowledgedAt)

  /* Снять ошибочно приложенный файл: не то сопроводительное, чужой скан,
     черновик вместо накладной. Причина обязательна — снятие без объяснения в
     журнале неотличимо от потери файла. */
  const dropFile = async (name: string) => {
    const reason = window.prompt(t(lang, 'Причина снятия приложения', 'Why remove the attachment?'), '')
    if (reason == null || reason.trim() === '') return
    setBusy(true)
    try {
      await request('DELETE', `/transmittals/${encodeURIComponent(code)}/files`
        + `?name=${encodeURIComponent(name)}&reason=${encodeURIComponent(reason.trim())}`,
        undefined, { idempotencyKey: crypto.randomUUID() })
      await receipt.refetch()
      toast({ tone: 'success', title: t(lang, 'Приложение снято', 'Attachment removed'), text: name })
    } catch (e) {
      toast({ tone: 'danger', title: t(lang, 'Не снято', 'Not removed'), text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  const upload = async (file: File) => {
    setBusy(true)
    try {
      await uploadTrmFile(code, file, kind)
      await receipt.refetch()
      toast({ tone: 'success', title: t(lang, 'Файл приложен', 'File attached'), text: file.name })
    } catch (e) {
      toast({ tone: 'danger', title: t(lang, 'Файл не приложен', 'Attach failed'), text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel
      title={t(lang, 'Передача', 'Handover')}
      action={iAmRecipient && !acknowledged ? (
        <Button variant="primary" size="sm" disabled={ack.isPending} iconLeft={<Icon name="check-check" size={13} />}
                onClick={() => ack.mutate({}, {
                  onSuccess: () => toast({ tone: 'success', title: t(lang, 'Получение подтверждено', 'Receipt acknowledged') }),
                })}>
          {ack.isPending ? t(lang, 'Подтверждаем…', 'Confirming…') : t(lang, 'Подтвердить получение', 'Acknowledge receipt')}
        </Button>
      ) : !isVendor && data?.dir === 'out' && !acknowledged ? (
        <Button variant="secondary" size="sm" iconLeft={<Icon name="clipboard-check" size={13} />}
                onClick={() => setOfflineOpen((v) => !v)}
                title={t(lang, 'Пакет передан вне системы: отметить получение за подрядчика',
                              'Handed over outside the system: record the receipt on the contractor’s behalf')}>
          {t(lang, 'Отметить получение', 'Record receipt')}
        </Button>
      ) : null}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
          <span style={{ display: 'inline-flex', color: acknowledged ? 'var(--green-600)' : 'var(--text-subtle)' }}>
            <Icon name={acknowledged ? 'check-check' : 'clock'} size={15} />
          </span>
          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            {acknowledged
              ? t(lang, `Получение подтвердил ${data?.acknowledgedBy ?? ''} · ${(data?.acknowledgedAt ?? '').slice(0, 16).replace('T', ' ')}`,
                     `Acknowledged by ${data?.acknowledgedBy ?? ''} · ${(data?.acknowledgedAt ?? '').slice(0, 16).replace('T', ' ')}`)
              : t(lang, 'Получение не подтверждено', 'Receipt not acknowledged yet')}
          </span>
        </div>

        {offlineOpen && !acknowledged ? (
          <div style={{ display: 'grid', gap: 8, padding: '10px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
            <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Отметка не выдаёт себя за подтверждение подрядчика: в журнале останется, кто её поставил.',
                       'The record does not pose as the contractor’s own: the log keeps who entered it.')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 8, alignItems: 'end' }}>
              <DateInput label={t(lang, 'Дата получения', 'Date of receipt')} value={receiptDate} onChange={setReceiptDate} />
              <Input label={t(lang, 'Кто получил', 'Received by')} value={receiptBy}
                     onChange={(e) => setReceiptBy(e.target.value)}
                     placeholder={t(lang, 'Например: И. Петров, вх. № 55', 'e.g. I. Petrov, in. no. 55')} />
              <Button variant="primary" size="sm" disabled={offline.isPending || !receiptDate.trim() || !receiptBy.trim()}
                      onClick={() => offline.mutate({ date: receiptDate.trim(), by: receiptBy.trim() }, {
                        onSuccess: () => { setOfflineOpen(false); toast({ tone: 'success', title: t(lang, 'Получение отмечено', 'Receipt recorded') }) },
                        onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не отмечено', 'Not recorded'), text: e.message }),
                      })}>{t(lang, 'Отметить', 'Record')}</Button>
            </div>
          </div>
        ) : null}

        {(data?.files ?? []).map((f) => (
          <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
            <Icon name="file-text" size={14} />
            <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <Badge tone="neutral">{t(lang, TRM_FILE_KIND[f.kind]?.[0] ?? f.kind, TRM_FILE_KIND[f.kind]?.[1] ?? f.kind)}</Badge>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{fileSize(f.size, lang)}</span>
            <a href={f.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', color: 'var(--text-link)' }} title={t(lang, 'Скачать', 'Download')}>
              <Icon name="download" size={14} />
            </a>
            {/* Ошибочно приложенный файл: не то сопроводительное, чужой скан,
                черновик вместо накладной. Подтверждённое письмо сервер закроет —
                под подписью получателя состав приложений не меняют. */}
            {acknowledged ? null : (
              <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={12} />} disabled={busy}
                      title={t(lang, 'Снять приложение письма', 'Remove the letter attachment')}
                      onClick={() => void dropFile(f.name)}>{t(lang, 'Снять', 'Remove')}</Button>
            )}
          </div>
        ))}
        {!receipt.isPending && (data?.files ?? []).length === 0 ? (
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Сопроводительных файлов нет.', 'No accompanying files.')}
          </div>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Select size="sm" value={kind} onChange={(e) => setKind(e.target.value as 'cover' | 'signed' | 'other')} block={false}>
            <option value="cover">{t(lang, 'Сопроводительное письмо', 'Cover letter')}</option>
            <option value="signed">{t(lang, 'Подписанный экземпляр', 'Signed copy')}</option>
            <option value="other">{t(lang, 'Прочее', 'Other')}</option>
          </Select>
          <input ref={fileRef} type="file" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = '' }} />
          <Button variant="secondary" size="sm" disabled={busy} iconLeft={<Icon name="upload" size={13} />}
                  onClick={() => fileRef.current?.click()}>
            {busy ? t(lang, 'Загрузка…', 'Uploading…') : t(lang, 'Приложить файл', 'Attach file')}
          </Button>
        </div>
      </div>
    </Panel>
  )
}

// ---- TRM card ----------------------------------------------------------
export function TrmCard({ id, lang, onClose, onOpenDoc, onOpenReview, onOpenCrs }: { id: string; lang: Lang; onClose: () => void; onOpenDoc?: (no: string) => void; onOpenReview?: () => void; onOpenCrs?: (code: string) => void }) {
  const trmQuery = useItem<Transmittal>('transmittals', id)
  /* Состав трансмиттала ограничен по замыслу — это партия документов, а не
     реестр, поэтому одной страницы достаточно. Документ партии лежит либо в
     реестре проекта, либо в реестре поставщика: спрашиваются оба. */
  /** Состав пакета из его позиций: ревизия и версия зафиксированы выпуском. */
  const itemsQuery = useSub<{ rows: { no: string; title: string | null; rev: string | null; ver: number | null; code: string | null
                                      medium: 'el' | 'paper' | 'both'; copies: number }[] }>(
    `/transmittals/${encodeURIComponent(id)}/items`)
  const items = itemsQuery.data?.rows ?? []
  /* Носитель и число экземпляров печатает бланк, а домен их не хранил: в
     бумаге дописывали от руки. Правятся до отправки — по отправленному бланку
     подписан приём. */
  /* Состав черновика: дописать забытый документ и снять лишний. Прежде это
     значило «удалить черновик и набрать состав заново». */
  const dropItem = useCommand<{ doc: string }>({
    method: 'DELETE',
    path: (a) => `/transmittals/${encodeURIComponent(id)}/items/${encodeURIComponent(a.doc)}`,
    invalidate: () => [qk.sub(`/transmittals/${encodeURIComponent(id)}/items`), qk.all('transmittals'), qk.all('audit')],
  })
  const setItem = useCommand<{ doc: string; medium?: string; copies?: number }>({
    method: 'PATCH',
    path: (a) => `/transmittals/${encodeURIComponent(id)}/items/${encodeURIComponent(a.doc)}`,
    body: (a) => ({ medium: a.medium, copies: a.copies }),
    invalidate: () => [qk.sub(`/transmittals/${encodeURIComponent(id)}/items`), qk.all('audit')],
  })
  const send = useCommand<void>({
    path: () => `/transmittals/${encodeURIComponent(id)}/send`,
    invalidate: () => [qk.all('transmittals'), qk.all('audit'), ...inboxKeys],
  })
  const docsQuery = useList<Doc>('docs', { page: 0, pageSize: 200, filters: { trm: [id] }, sort: [{ id: 'no', desc: false }] })
  const vdocsQuery = useList<Vdoc>('vdocs', { page: 0, pageSize: 200, filters: { trm: [id] }, sort: [{ id: 'no', desc: false }] })
  /* Удаление — операция администратора: кнопка гейтится тем же правом, каким
     сервер отобьёт команду (area admin, уровень rw). */
  const isAdmin = useMe().data?.perms?.admin === 'rw'
  /* Подрядчик видит свой пакет в том же окне: отзыв, приёмку и перевыпуск
     ведёт сторона заказчика, и кнопки, отвечающие ему 403, ему не нужны. */
  const external = Boolean(useMe().data?.user.vendor)
  const [removing, setRemoving] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [intake, setIntake] = useState(false)
  const [editingLetter, setEditingLetter] = useState(false)
  const [closing, setClosing] = useState(false)
  const [addingItems, setAddingItems] = useState(false)
  /* Реквизиты письма и итог входного контроля: отдельным ответом, а не в
     контракте списков — там общий с моком контракт, а это поля карточки. */
  const letter = useSub<{
    fao: string | null; cc: string | null; authority: string | null; remarks: string | null
    partnerNo: string | null; cancelReason: string | null; repliesTo: string | null
    intakeOn: string | null; intakeBy: string | null; intakeNote: string | null
  }>(`/transmittals/${encodeURIComponent(id)}/letter`)
  const reissue = useCommand<void, { id: string }>({
    path: () => `/transmittals/${encodeURIComponent(id)}/reissue`,
    invalidate: () => [qk.all('transmittals'), qk.all('audit'), ...inboxKeys],
  })
  /* Листы CRS, ушедшие этим трансмитталом: возвратный пакет и есть реестр
     замечаний. Поле trmOut фильтруемо в реестре листов — контракт не меняется. */
  const sheetsQuery = useList<{ id: string; doc: string; rev: string | null; ver: number | null; code: ReviewCode; status: string }>(
    'crs-sheets', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }], filters: { trmOut: [id] } })
  const [printing, setPrinting] = useState<string | null>(null)
  const [packing, setPacking] = useState(false)
  const [blankBusy, setBlankBusy] = useState(false)

  const trm = trmQuery.data
  if (!trm) return (
    <Drawer open onClose={onClose} title={null}>
      <CardState lang={lang} error={trmQuery.isError} />
    </Drawer>
  )
  const docs: (Doc | Vdoc)[] = [...(docsQuery.data?.rows ?? []), ...(vdocsQuery.data?.rows ?? [])]
  const total = (docsQuery.data?.total ?? 0) + (vdocsQuery.data?.total ?? 0)
  const inb = trm.dir === 'in'
  const sheets = sheetsQuery.data?.rows ?? []

  const runPackage = async () => {
    setPacking(true)
    try {
      const res = await downloadTrmPackage(trm, docs, lang)
      toast({
        tone: res.failed.length ? 'danger' : 'success',
        title: t(lang, 'Пакет собран', 'Package built'),
        text: res.failed.length
          ? t(lang, `Не удалось: ${res.failed.join(', ')}`, `Failed: ${res.failed.join(', ')}`)
          : `${trm.id} · ${res.files} ${t(lang, 'файл(ов)', 'file(s)')} · CRS ${res.sheets}`,
      })
    } catch (e) {
      toast({ tone: 'danger', title: t(lang, 'Пакет не собран', 'Package failed'), text: e instanceof Error ? e.message : String(e) })
    } finally {
      setPacking(false)
    }
  }
  return (
    <Drawer open onClose={onClose} title={null}>
      <CardHead>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
          <span style={{ display: 'inline-grid', placeItems: 'center', width: 26, height: 26, borderRadius: 'var(--radius-sm)', background: inb ? 'var(--blue-50)' : 'var(--amber-50)', color: inb ? 'var(--blue-600)' : 'var(--amber-600)', border: '1px solid ' + (inb ? 'var(--blue-100)' : 'var(--amber-100)') }}><Icon name={inb ? 'arrow-down-left' : 'arrow-up-right'} size={15} /></span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 600, color: 'var(--text-strong)' }}>{trm.id}</span>
          <Badge tone={TRM_STATUS[trm.status] ?? 'neutral'} dot>{trm.status}</Badge>
          <Purpose value={trm.purpose} /><CodeChip code={trm.code} size="sm" />
        </div>
        <div style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{trm.subject}</div>
        {/* Ряд переносится по строкам, как в шапке карточки документа: действий
            у входящего пакета до девяти — около 1168 px, а панель шириной 579
            и шире не станет. Без переноса «Править письмо», «Закрыть без
            рассмотрения», «Входной контроль» и «Аннулировать» лежали за правым
            краем экрана. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {/* Манифест с подписанными ссылками + zip в браузере: байты не идут
              через приложение — тот же инвариант, что у одиночного скачивания. */}
          <Button variant="secondary" size="sm" disabled={packing || docsQuery.isPending || vdocsQuery.isPending}
                  iconLeft={<Icon name="download" size={14} />}
                  onClick={() => void runPackage()}>{packing ? t(lang, 'Собираем…', 'Building…') : t(lang, 'Скачать пакет', 'Download package')}</Button>
          {/* Сопроводительная форма по шаблону: шапка + состав, на языке интерфейса. */}
          <Button variant="secondary" size="sm" iconLeft={<Icon name="file-text" size={14} />}
                  disabled={docsQuery.isPending || vdocsQuery.isPending}
                  onClick={() => exportTrmForm(trm, docs, lang)}>{t(lang, 'Форма TRM (xlsx)', 'TRM form (xlsx)')}</Button>
          {/* Бланк заказчика собирает сервер: у него лежит сам бланк, и он же
              знает, каким он бывает для входящего и для исходящего. */}
          <Button variant="secondary" size="sm" iconLeft={<Icon name="printer" size={14} />}
                  disabled={blankBusy}
                  title={t(lang, 'Бланк заказчика, заполненный данными пакета', "The customer's blank filled with the package data")}
                  onClick={() => {
                    setBlankBusy(true)
                    saveFile(`/transmittals/${encodeURIComponent(trm.id)}/form`)
                      .then((name) => toast({ tone: 'success', title: t(lang, 'Бланк собран', 'Blank ready'), text: name }))
                      .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Бланк не собран', 'Blank failed'),
                        text: e instanceof Error ? e.message : String(e) }))
                      .finally(() => setBlankBusy(false))
                  }}>{blankBusy ? t(lang, 'Собираем…', 'Building…') : t(lang, 'Бланк заказчика', "Customer's blank")}</Button>
          {inb ? <Button variant="primary" size="sm" iconLeft={<Icon name="clipboard-check" size={14} />} onClick={() => onOpenReview && onOpenReview()}>{t(lang, 'К рассмотрению', 'Open review')}</Button> : null}
          {/* Правка письма: тема, срок и реквизиты бланка. Прежде опечатка в
              теме означала «аннулировать и выпустить заново». */}
          {!external && trm.status !== 'Аннулирован' ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={14} />}
                    onClick={() => setEditingLetter(true)}>{t(lang, 'Править письмо', 'Edit letter')}</Button>
          ) : null}
          {/* Черновик отправляется отдельной командой: между «собрал» и
              «отправил» стоит подпись, и до неё письмо ещё правится. */}
          {trm.status === 'Черновик' ? (
            <Button variant="primary" size="sm" iconLeft={<Icon name="send" size={14} />} disabled={send.isPending}
                    onClick={() => send.mutate(undefined, {
                      onSuccess: () => toast({ tone: 'success', title: t(lang, 'Отправлен', 'Sent'), text: trm.id }),
                    })}>{send.isPending ? t(lang, 'Отправляем…', 'Sending…') : t(lang, 'Отправить', 'Send')}</Button>
          ) : null}
          {/* Пакет «для сведения» рассмотрению не подлежит: его регистрируют и
              закрывают. Команда была на сервере, а кнопки не было — и для
              такого пакета проходили полный круг рассмотрения. */}
          {!external && inb && !['Закрыт', 'Аннулирован', 'Отклонён'].includes(trm.status) ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="check" size={14} />}
                    onClick={() => setClosing(true)}>{t(lang, 'Закрыть без рассмотрения', 'Close without a review')}</Button>
          ) : null}
          {/* Входной контроль: пакет принимают к рассмотрению или возвращают
              по формальным основаниям — комплектность, оформление, не та
              ревизия. Раньше вернуть пакет без единого технического замечания
              было нечем. */}
          {!external && inb && trm.status === 'Зарегистрирован' ? (
            <Button variant="primary" size="sm" iconLeft={<Icon name="clipboard-check" size={14} />}
                    onClick={() => setIntake(true)}>{t(lang, 'Входной контроль', 'Intake')}</Button>
          ) : null}
          {/* Перевыпуск отозванного: новый номер, прежний состав и реквизиты.
              Собирать пакет заново значило бы, что отзыв невозможен. */}
          {!external && !inb && trm.status === 'Аннулирован' ? (
            <Button variant="primary" size="sm" iconLeft={<Icon name="rotate-ccw" size={14} />} disabled={reissue.isPending}
                    onClick={() => reissue.mutate(undefined, {
                      onSuccess: (r) => toast({ tone: 'success', title: t(lang, 'Пакет перевыпущен', 'Reissued'),
                        text: t(lang, 'Черновик ' + r.id, 'Draft ' + r.id) }),
                      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не перевыпущен', 'Not reissued'), text: e.message }),
                    })}>{t(lang, 'Перевыпустить', 'Reissue')}</Button>
          ) : null}
          {/* Отзыв — рядовая операция документооборота: не тот получатель, не
              та ревизия, ошибочный код. Пакет остаётся в журнале с причиной,
              состав освобождается, перевыпуск идёт новым номером.

              Отзыв ведёт сторона заказчика: подрядчику кнопка не показывается,
              иначе она отвечала бы ему 403. */}
          {external || trm.status === 'Аннулирован' ? null : (
            <Button variant="secondary" size="sm" style={{ marginLeft: 'auto' }} iconLeft={<Icon name="x" size={14} />}
                    onClick={() => setCancelling(true)}>{t(lang, 'Аннулировать', 'Cancel')}</Button>
          )}
          {isAdmin ? <Button variant="danger" size="sm" iconLeft={<Icon name="trash-2" size={14} />} onClick={() => setRemoving(true)}>{t(lang, 'Удалить', 'Remove')}</Button> : null}
        </div>
      </CardHead>
      {removing ? <RemoveTrmDialog open code={trm.id} lang={lang} onClose={() => setRemoving(false)} onDone={onClose} /> : null}
      {cancelling ? <CancelTrmDialog open code={trm.id} lang={lang} onClose={() => setCancelling(false)} /> : null}
      {intake ? <IntakeDialog open code={trm.id} lang={lang} onClose={() => setIntake(false)} /> : null}
      {closing ? <CloseInboundDialog open code={trm.id} lang={lang} onClose={() => setClosing(false)} /> : null}
      {editingLetter ? (
        <LetterDialog open code={trm.id} draft={trm.status === 'Черновик'} lang={lang}
                      initial={{ subject: trm.subject, recipient: trm.to,
                                 due: trm.due, issuedOn: trm.date,
                                 fao: letter.data?.fao ?? null, cc: letter.data?.cc ?? null,
                                 authority: letter.data?.authority ?? null, remarks: letter.data?.remarks ?? null,
                                 partnerNo: letter.data?.partnerNo ?? null }}
                      onClose={() => setEditingLetter(false)} />
      ) : null}
      <div style={{ flex: 1, overflow: 'auto', padding: 20, background: 'var(--surface-page)', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Panel title={t(lang, 'Сведения о трансмиттале', 'Transmittal details')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, padding: '14px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
            <Field label={t(lang, 'Направление', 'Direction')}>{inb ? t(lang, 'Входящий', 'Incoming') : t(lang, 'Исходящий', 'Outgoing')}</Field>
            <Field label={t(lang, 'Отправитель', 'From')}>{trm.from}</Field>
            <Field label={t(lang, 'Получатель', 'To')}>{trm.to}</Field>
            <Field label={t(lang, 'Дата регистрации', 'Registered')} mono>{trm.date}</Field>
            <Field label={t(lang, 'Срок ответа', 'Due')} mono>{trm.due ? <span style={{ color: 'var(--date-forecast)' }}>{trm.due}</span> : '—'}</Field>
            <Field label={t(lang, 'Назначение выпуска', 'Purpose')}><Purpose value={trm.purpose} size="sm" /></Field>
            {/* Реквизиты письма: их набирают руками при выпуске и печатают в
                бланке, а до сих пор они не показывались нигде — как и причина
                отзыва с итогом входного контроля. */}
            {letter.data?.fao ? <Field label={t(lang, 'Кому (FAO)', 'FAO')}>{letter.data.fao}</Field> : null}
            {/* Копия — участник переписки: организация проекта, названная в
                копии, видит пакет и получает извещение о нём (V67). */}
            {letter.data?.cc ? <Field label={t(lang, 'Копии (CC)', 'CC')}>{letter.data.cc}</Field> : null}
            {letter.data?.authority ? <Field label={t(lang, 'Полномочия', 'Authority')}>{letter.data.authority}</Field> : null}
            {letter.data?.partnerNo ? <Field label={t(lang, '№ контрагента', 'Partner no.')} mono>{letter.data.partnerNo}</Field> : null}
            {letter.data?.repliesTo ? <Field label={t(lang, 'В ответ на', 'In reply to')} mono>{letter.data.repliesTo}</Field> : null}
            {letter.data?.remarks ? <Field label={t(lang, 'Отметки', 'Remarks')}>{letter.data.remarks}</Field> : null}
            {letter.data?.intakeOn ? (
              <Field label={t(lang, 'Входной контроль', 'Intake')}>
                {letter.data.intakeOn}{letter.data.intakeBy ? ' · ' + letter.data.intakeBy : ''}
                {letter.data.intakeNote ? ' · ' + letter.data.intakeNote : ''}
              </Field>
            ) : null}
            {letter.data?.cancelReason ? (
              <Field label={t(lang, 'Причина отзыва', 'Cancellation reason')}>
                <span style={{ color: 'var(--danger-ink)' }}>{letter.data.cancelReason}</span>
              </Field>
            ) : null}
          </div>
        </Panel>
        <TrmHandover code={trm.id} lang={lang} />
        {/* Состав читается из ПОЗИЦИЙ пакета, а не по полю «источник (TRM)» у
            документа: то поле пишет только подача, и у исходящего пакета
            состав выглядел пустым, хотя позиции есть. Ревизия и версия в
            позиции зафиксированы на момент выпуска. */}
        <Panel title={t(lang, 'Документы в составе TRM', 'Documents in this TRM')} count={items.length || total || trm.docs}
               action={trm.status === 'Черновик' && !external ? (
                 <Button variant="secondary" size="sm" iconLeft={<Icon name="file-plus" size={13} />}
                         onClick={() => setAddingItems(true)}>{t(lang, 'Дописать документы', 'Add documents')}</Button>
               ) : undefined}>
          {itemsQuery.isPending ? (
            <div style={note()}>{t(lang, 'Загрузка…', 'Loading…')}</div>
          ) : items.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((it) => (
                <div key={it.no} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', cursor: onOpenDoc ? 'pointer' : 'default' }}
                     {...clickable(() => onOpenDoc && onOpenDoc(it.no))}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-link)', width: 200 }}>{it.no}</span>
                  <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title ?? ''}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                    {t(lang, 'ред.', 'rev')} {it.rev ?? '—'}{it.ver != null ? ` · v${it.ver}` : ''}
                  </span>
                  {/* Носитель и экземпляры: колонки описи, по которой
                      подписывают приём. У черновика правятся, у отправленного
                      показываются. */}
                  {trm.status === 'Черновик' && !external ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} role="presentation"
                         onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <Select size="sm" block={false} style={{ width: 120 }} value={it.medium}
                              disabled={setItem.isPending}
                              onChange={(e) => setItem.mutate({ doc: it.no, medium: e.target.value })}>
                        <option value="el">{t(lang, 'Электронно', 'Electronic')}</option>
                        <option value="paper">{t(lang, 'Бумага', 'Paper')}</option>
                        <option value="both">{t(lang, 'Бумага и эл.', 'Paper & el.')}</option>
                      </Select>
                      <Input size="sm" style={{ width: 56 }} value={String(it.copies)}
                             title={t(lang, 'Экземпляров', 'Copies')}
                             onChange={(e) => {
                               const n = Number.parseInt(e.target.value, 10)
                               if (n >= 1 && n <= 999) setItem.mutate({ doc: it.no, copies: n })
                             }} />
                    </div>
                  ) : (
                    <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
                      {MEDIUM_NAME(it.medium, lang)} · {it.copies} {t(lang, 'экз.', 'copies')}
                    </span>
                  )}
                  <CodeChip code={(it.code ?? null) as ReviewCode | null} size="sm" />
                  {trm.status === 'Черновик' && !external && items.length > 1 ? (
                    <span role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={13} />} disabled={dropItem.isPending}
                              title={t(lang, 'Снять позицию из состава черновика', 'Drop the item from the draft')}
                              onClick={() => dropItem.mutate({ doc: it.no }, {
                                onError: (err) => toast({ tone: 'danger', title: t(lang, 'Не снято', 'Not dropped'), text: err.message }),
                              })}>{t(lang, 'Снять', 'Drop')}</Button>
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            /* Пустой состав — не обязательно возврат: пакетом уходит и шаблон
               реестра, и одно сопроводительное письмо. Возвратным пакет делают
               ушедшие им листы CRS, по ним и говорим. */
            <DocList
              pending={docsQuery.isPending || vdocsQuery.isPending}
              error={docsQuery.isError || vdocsQuery.isError}
              rows={docs} lang={lang} onOpenDoc={onOpenDoc}
              empty={<div style={note()}>{sheets.length
                ? t(lang, 'Возвратный трансмиттал — содержит CRS и сопроводительные документы по предыдущей партии.', 'Return transmittal — carries CRS and cover documents for the previous batch.')
                : t(lang, 'Документов в составе нет: пакет несёт только сопроводительные файлы.', 'No documents in this package: it carries cover files only.')}</div>} />
          )}
        </Panel>
        {/* Реестр замечаний пакета: листы, ушедшие этим трансмитталом. */}
        {sheets.length ? (
          <Panel title={t(lang, 'Листы замечаний CRS', 'CRS sheets')} count={sheets.length}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sheets.map((s) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
                  {/* Код листа — ссылка на экран замечаний: он понимает код
                      листа, и у подрядчика ведёт в его портал (ccrs). */}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}><CodeLink code={s.id} onOpen={onOpenCrs} /></span>
                  <CodeChip code={s.code} size="sm" />
                  <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{s.doc} · {t(lang, 'рев.', 'rev.')} {revVer(s.rev, s.ver)}</span>
                  <Button variant="secondary" size="sm" iconLeft={<Icon name="printer" size={13} />} onClick={() => setPrinting(s.id)}>{t(lang, 'Печать', 'Print')}</Button>
                </div>
              ))}
            </div>
          </Panel>
        ) : null}
      </div>
      {addingItems ? (
        <AddItemsDialog open code={trm.id} vendor={trm.to} present={items.map((it) => it.no)} lang={lang}
                        onClose={() => setAddingItems(false)} />
      ) : null}
      <SheetPrint code={printing} lang={lang} onClose={() => setPrinting(null)} />
    </Drawer>)

}

/**
 * Удаление трансмиттала. Решает сервер: удаляемы только состояния до
 * рассмотрения — запущенный маршрут, замечания, листы CRS или документы
 * подачи отбивают команду понятным 409, который покажется тостом. Причина
 * обязательна: удаление в enterprise-журнале без причины не объяснимо.
 */
/**
 * Аннулировать пакет.
 *
 * <p>Отличается от удаления: пакет остаётся в журнале со своим номером и
 * причиной, состав возвращается в прежнее состояние, а у возвратного пакета
 * снимаются коды и листы. Удаление — операция администратора и только для
 * пакета, который ещё ничего не задел.
 */
/* Входной контроль: пакет принимают к рассмотрению или возвращают по
   формальным основаниям. Отметка о комплектности остаётся в карточке — по ней
   потом разбирают, почему пакет вернулся. */
/**
 * Закрыть входящий пакет без рассмотрения.
 *
 * <p>Пакет «для сведения» рассмотрению не подлежит: его регистрируют и
 * закрывают. Причина обязательна — закрытие без объяснения в журнале
 * неотличимо от потерянного пакета. Пакет с замечаниями сервер закрыть не
 * даст: их выпускают листом, а не прячут закрытием.
 */
function CloseInboundDialog({ open, code, lang, onClose }: { open: boolean; code: string; lang: Lang; onClose: () => void }) {
  const [reason, setReason] = useState('')
  const close = useCommand<{ reason: string }>({
    path: () => `/transmittals/${encodeURIComponent(code)}/close`,
    body: (a) => a,
    invalidate: () => [qk.all('transmittals'), qk.item('transmittals', code), qk.all('audit'), ...inboxKeys],
  })
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Закрыть пакет без рассмотрения', 'Close without a review')}>
      <div style={{ width: 460, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Так закрывают пакет «для сведения»: рассмотрению он не подлежит. Пакет, по которому уже есть замечания, сервер закрыть не даст — их выпускают листом.',
                   'This closes an information-only package. A package with comments cannot be closed: they go out on a CRS sheet.')}
        </div>
        <Input label={t(lang, 'Причина', 'Reason')} value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder={t(lang, 'напр. пакет «для сведения», рассмотрение не требуется', 'e.g. information only, no review required')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={reason.trim() === '' || close.isPending}
                  title={reason.trim() ? undefined : t(lang, 'Нужна причина', 'A reason is required')}
                  onClick={() => close.mutate({ reason: reason.trim() }, {
                    onSuccess: () => { onClose(); toast({ tone: 'success', title: t(lang, 'Пакет закрыт', 'Package closed'), text: code }) },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не закрыт', 'Not closed'), text: e.message }),
                  })}>{t(lang, 'Закрыть пакет', 'Close')}</Button>
        </div>
      </div>
    </Dialog>
  )
}

function IntakeDialog({ open, code, lang, onClose }: { open: boolean; code: string; lang: Lang; onClose: () => void }) {
  /* Итог входного контроля построчный (V76): у каждой возвращаемой строки своё
     основание из справочника и свой комментарий. Прежде диалог слал устаревшее
     тело — «принято/возвращено» с одной фразой на весь пакет, — и сервер
     читал его как «возвратов нет»: нажатие «Вернуть» ПРИНИМАЛО пакет, а тост
     говорил обратное. */
  const [mode, setMode] = useState<'accept' | 'return'>('accept')
  const [note, setNote] = useState('')
  const [wholeReason, setWholeReason] = useState('')
  const reasons = useSub<{ rows: { code: string; name: string }[] }>(
    open ? '/transmittals/intake-reasons' : null).data?.rows ?? []
  /* Возвращаемые строки: основание и комментарий на каждую. Одна фраза на
     четыре строки заставляла подрядчика гадать, что именно исправлять. */
  const [returned, setReturned] = useState<Record<string, { reason: string; note: string }>>({})
  const items = useSub<{ rows: { no: string; title: string | null; rev: string | null }[] }>(
    open ? `/transmittals/${encodeURIComponent(code)}/items` : null)
  const rows = items.data?.rows ?? []
  const toggle = (no: string) => setReturned((prev) => {
    const next = { ...prev }
    if (next[no]) delete next[no]
    else next[no] = { reason: reasons[0]?.code ?? '', note: '' }
    return next
  })
  const setRow = (no: string, patch: Partial<{ reason: string; note: string }>) =>
    setReturned((prev) => ({ ...prev, [no]: { ...(prev[no] ?? { reason: '', note: '' }), ...patch } }))

  const act = useCommand<{ returned?: { docNo: string; reason: string; note: string | null }[]
                           reason?: string; note?: string },
    { status: string; returnTrm?: string }>({
    path: () => `/transmittals/${encodeURIComponent(code)}/register`,
    body: (a) => a,
    invalidate: () => [qk.all('transmittals'), qk.all('docs'), qk.all('vdocs'), qk.all('audit'),
                       qk.sub(`/transmittals/${encodeURIComponent(code)}/items`), ...inboxKeys],
  })
  const picked = Object.entries(returned)
  const ready = mode === 'return'
    ? wholeReason !== ''
    : picked.every(([, r]) => r.reason !== '')
  const submit = () => act.mutate(
    mode === 'return'
      ? { reason: wholeReason, note: note.trim() || undefined }
      : { returned: picked.map(([docNo, r]) => ({ docNo, reason: r.reason, note: r.note.trim() || null })) },
    {
      onSuccess: (res) => {
        onClose()
        toast({
          tone: 'success',
          title: mode === 'return' ? t(lang, 'Пакет возвращён', 'Returned')
            : picked.length ? t(lang, 'Проверен, часть возвращена', 'Checked, some rows returned')
              : t(lang, 'Пакет проверен', 'Package checked'),
          text: res.returnTrm ? `${code} · ${t(lang, 'письмо', 'letter')} ${res.returnTrm}` : code,
        })
      },
      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не вышло', 'Failed'), text: e.message }),
    })

  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Входной контроль пакета', 'Package intake')}>
      <div style={{ width: 620, maxWidth: '100%', display: 'grid', gap: 12 }}>
        {/* У пакета без состава — письма, шаблона реестра — строк нет, и
            обещание «возвращённые строки» ему не про что. */}
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {rows.length
            ? t(lang, 'Проверенный пакет отправляют по матрице; возвращённые строки уезжают подрядчику возвратным письмом с основанием.',
                      'A checked package is sent through the matrix; returned rows go back to the contractor by a return letter stating the ground.')
            : t(lang, 'В пакете нет документов: проверяются сопроводительные файлы. Возврат уходит подрядчику письмом с основанием.',
                      'The package carries no documents: the cover files are what is checked. A return goes back to the contractor by a letter stating the ground.')}
        </div>
        <Select label={t(lang, 'Итог контроля', 'Outcome')} value={mode}
                onChange={(e) => setMode(e.target.value as 'accept' | 'return')}>
          <option value="accept">{t(lang, 'Принять (можно вернуть отдельные строки)', 'Accept (individual rows may be returned)')}</option>
          <option value="return">{t(lang, 'Вернуть пакет целиком', 'Return the whole package')}</option>
        </Select>
        {mode === 'return' ? (
          <>
            <Select label={t(lang, 'Основание возврата', 'Ground for return')} value={wholeReason}
                    onChange={(e) => setWholeReason(e.target.value)}>
              <option value="">{t(lang, '— выберите —', '— pick one —')}</option>
              {reasons.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
            </Select>
            <Input label={t(lang, 'Комментарий', 'Comment')} value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder={t(lang, 'необязательно', 'optional')} />
          </>
        ) : rows.length ? (
          <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
            <div style={{ padding: '7px 10px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Отметьте строки, которые уходят обратно; остальные пойдут по матрице',
                       'Tick the rows that go back; the rest goes through the matrix')}
            </div>
            <div style={{ maxHeight: 260, overflow: 'auto' }}>
              {rows.map((r) => (
                <div key={r.no} style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!returned[r.no]} onChange={() => toggle(r.no)} />
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)', width: 170 }}>{r.no}</span>
                    <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title ?? ''}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-subtle)' }}>{r.rev ?? '—'}</span>
                  </label>
                  {returned[r.no] ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 8, margin: '6px 0 2px 26px' }}>
                      <Select size="sm" value={returned[r.no]?.reason ?? ''}
                              onChange={(e) => setRow(r.no, { reason: e.target.value })}>
                        <option value="">{t(lang, '— основание —', '— ground —')}</option>
                        {reasons.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                      </Select>
                      <Input size="sm" value={returned[r.no]?.note ?? ''}
                             onChange={(e) => setRow(r.no, { note: e.target.value })}
                             placeholder={t(lang, 'комментарий к строке', 'comment for this row')} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant={mode === 'return' ? 'danger' : 'primary'} disabled={!ready || act.isPending}
                  title={ready ? undefined : t(lang, 'У возврата обязательно основание', 'A return needs a ground')}
                  onClick={submit}>
            {mode === 'return' ? t(lang, 'Вернуть пакет', 'Return package')
              : picked.length ? t(lang, `Принять, вернуть ${picked.length}`, `Accept, return ${picked.length}`)
                : t(lang, 'Принять', 'Accept')}
          </Button>
        </div>
      </div>
    </Dialog>)
}

function CancelTrmDialog({ open, onClose, code, lang }: { open: boolean; onClose: () => void; code: string; lang: Lang }) {
  const [reason, setReason] = useState('')
  const cancel = useCommand<{ reason: string }>({
    path: () => `/transmittals/${encodeURIComponent(code)}/cancel`,
    body: (a) => ({ reason: a.reason }),
    invalidate: () => [
      qk.all('transmittals'), qk.all('docs'), qk.all('vdocs'), qk.all('crs'),
      qk.all('crs-sheets'), qk.all('audit'), ...inboxKeys,
    ],
  })
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Аннулировать трансмиттал', 'Cancel transmittal')}>
      <div style={{ width: 460, maxWidth: '100%' }}>
        <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang,
             `${code} останется в журнале с причиной. Состав вернётся в прежнее состояние: подача освободит документы, возвратный пакет снимет коды и вернёт листы в черновики. Перевыпуск идёт новым номером.`,
             `${code} stays in the journal with its reason. The content returns to its previous state: a submission releases its documents, a return package drops the codes and moves the sheets back to drafts. Re-issue takes a new number.`)}
        </div>
        <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Причина', 'Reason')}</label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t(lang, 'например: не тот получатель', 'e.g. wrong recipient')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose} disabled={cancel.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="danger" disabled={cancel.isPending || !reason.trim()} onClick={() => cancel.mutate({ reason: reason.trim() }, {
            onSuccess: () => {
              toast({ tone: 'info', title: t(lang, 'Трансмиттал аннулирован', 'Transmittal cancelled'), text: code })
              onClose()
            },
          })}>{cancel.isPending ? t(lang, 'Аннулируем…', 'Cancelling…') : t(lang, 'Аннулировать', 'Cancel it')}</Button>
        </div>
      </div>
    </Dialog>
  )
}

function RemoveTrmDialog({ open, onClose, code, lang, onDone }: { open: boolean; onClose: () => void; code: string; lang: Lang; onDone: () => void }) {
  const [reason, setReason] = useState('')
  const remove = useCommand<{ reason: string }>({
    path: () => `/transmittals/${encodeURIComponent(code)}/remove`,
    body: (a) => ({ reason: a.reason }),
    invalidate: () => [
      qk.all('transmittals'), qk.all('docs'), qk.all('vdocs'), qk.all('audit'), ...inboxKeys,
    ],
  })
  const submit = () => {
    remove.mutate({ reason: reason.trim() }, {
      onSuccess: () => {
        toast({ tone: 'info', title: t(lang, 'Трансмиттал удалён', 'Transmittal removed'), text: `${code} ${t(lang, 'больше не в журнале', 'left the journal')}` })
        setReason('')
        onDone()
      },
    })
  }
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Удалить трансмиттал', 'Remove transmittal')}>
      <div style={{ width: 460, maxWidth: '100%' }}>
        <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang,
             `${code} исчезнет из журнала вместе с составом. История останется в аудите. Трансмиттал с запущенным рассмотрением или документами подачи сервер не удалит.`,
             `${code} leaves the journal together with its items. History stays in the audit log. A transmittal with a running review or submitted documents will be refused.`)}
        </div>
        <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Причина', 'Reason')}</label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t(lang, 'например: выпущен по ошибке', 'e.g. issued by mistake')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="danger" iconLeft={<Icon name="trash-2" size={14} />} disabled={reason.trim() === '' || remove.isPending}
                  title={reason.trim() ? undefined : t(lang, 'Причина обязательна', 'A reason is required')} onClick={submit}>
            {remove.isPending ? t(lang, 'Удаление…', 'Removing…') : t(lang, 'Удалить', 'Remove')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ---- MDR card ----------------------------------------------------------
/**
 * Удаление реестра. Реестр с действующими документами сервер не отдаст: у
 * документов номер выведен из его области и дисциплины, и остались бы записи,
 * ссылающиеся на то, чего нет. Предупреждение показывается заранее, но решает
 * сервер — он же считает документы.
 */
/**
 * Правка карточки реестра.
 *
 * <p>Команда на сервере была, а нажать её было негде: наименование,
 * ответственный, дисциплина, стадия и объект правились только в базе. Контракт
 * и подрядчик здесь не правятся намеренно — реестр заведён на контракт, и смена
 * стороны у наполненного реестра означала бы передачу его документов другой
 * организации, а не правку карточки.
 */
function EditRegisterDialog({ open, onClose, mdr, lang }: { open: boolean; onClose: () => void; mdr: Mdr; lang: Lang }) {
  const [title, setTitle] = useState(mdr.title)
  const [owner, setOwner] = useState(mdr.owner)
  const [stage, setStage] = useState(mdr.stage ?? '')
  const [area, setArea] = useState(mdr.area ?? '')
  const stageRows = useDictRows('stage')
  /* Объект выбирается прямо — титул к реестру отношения не имеет: с V90 титул
     принадлежит строке, а у реестра остаётся объект. Справочник объектов
     большой, поэтому выбор идёт поиском. */
  const areaRows = useDictRows('area')
  const save = useCommand<{ title?: string; owner?: string; stage?: string; area?: string }>({
    method: 'PATCH',
    path: () => `/mdrs/${encodeURIComponent(mdr.id)}`,
    body: (a) => a,
    invalidate: () => [qk.all('mdrs'), qk.item('mdrs', mdr.id), qk.all('docs'), qk.all('audit')],
  })
  /* Уходит только изменённое: непереданное поле сервер не трогает, и правка
     наименования не должна переписывать стадию заодно. */
  const changed = {
    ...(title.trim() && title !== mdr.title ? { title: title.trim() } : {}),
    ...(owner.trim() && owner !== mdr.owner ? { owner: owner.trim() } : {}),
    ...(stage && stage !== (mdr.stage ?? '') ? { stage } : {}),
    ...(area !== (mdr.area ?? '') ? { area } : {}),
  }
  const dirty = Object.keys(changed).length > 0
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Изменить реестр', 'Edit register')}>
      <div style={{ width: 520, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <Input label={t(lang, 'Наименование', 'Title')} value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input label={t(lang, 'Ответственный', 'Owner')} value={owner} onChange={(e) => setOwner(e.target.value)} />
        {/* Дисциплины у реестра нет: она свойство СТРОКИ. Реестр по контракту
            идёт по всем дисциплинам сразу, и поле обещало обратное. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <Select label={t(lang, 'Стадия', 'Stage')} value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">{t(lang, '— не задана —', '— none —')}</option>
            {stageRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}
          </Select>
        </div>
        <DictPicker lang={lang} label={t(lang, 'Объект проекта', 'Project area')} value={area}
                    options={areaRows.map((r) => ({ code: r.code, name: r.name }))}
                    onPick={setArea}
                    emptyLabel={t(lang, '— не задан —', '— none —')}
                    hint={t(lang, 'Значение по умолчанию для строк реестра: строка со своим объектом его перебивает.',
                                  'The default for the register rows: a row with its own area overrides it.')} />
        {/* Стадия входит в номер документа, а выданные номера не переписываются:
            сказать об этом надо до нажатия, а не после. */}
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Стадия и объект входят в номер документа. Уже выданные номера не меняются — правило действует на новые строки.',
                   'Stage and area go into the document number. Numbers already issued stay as they are — the rule applies to new rows.')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={!dirty || save.isPending}
                  title={dirty ? undefined : t(lang, 'Ничего не изменено', 'Nothing changed')}
                  onClick={() => save.mutate(changed, {
                    onSuccess: () => { onClose(); toast({ tone: 'success', title: t(lang, 'Реестр изменён', 'Register updated'), text: mdr.id }) },
                  })}>{save.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}</Button>
        </div>
      </div>
    </Dialog>)
}

function RemoveRegisterDialog({ open, onClose, kind, code, docs, lang, onDone }: { open: boolean; onClose: () => void; kind: 'mdrs' | 'vdrs'; code: string; docs: number; lang: Lang; onDone: () => void }) {
  const [reason, setReason] = useState('')
  const remove = useCommand<{ reason: string }>({
    path: () => `/${kind}/${encodeURIComponent(code)}/remove`,
    body: (a) => ({ reason: a.reason }),
    invalidate: () => [
      qk.all(kind), qk.all(kind === 'mdrs' ? 'docs' : 'vdocs'), qk.all('audit'), ...inboxKeys,
    ],
  })
  const submit = () => {
    remove.mutate({ reason: reason.trim() }, {
      onSuccess: () => {
        toast({ tone: 'info', title: 'Реестр удалён', text: `${code} больше не в проекте` })
        setReason('')
        onDone()
      },
    })
  }
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Удалить реестр', 'Remove register')}>
      <div style={{ width: 460, maxWidth: '100%' }}>
        <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {docs > 0
            ? t(lang,
                `В реестре ${docs} документов. Удалить его можно только пустым — сначала аннулируйте документы или перенесите их.`,
                `The register holds ${docs} documents. It can be removed only when empty — void or move them first.`)
            : t(lang,
                `${code} исчезнет из проекта. История реестра останется в журнале.`,
                `${code} leaves the project. Its history stays in the audit log.`)}
        </div>
        <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Причина', 'Reason')}</label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t(lang, 'например: заведён по ошибке', 'e.g. created by mistake')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="danger" iconLeft={<Icon name="trash-2" size={14} />} disabled={remove.isPending} onClick={submit}>
            {remove.isPending ? t(lang, 'Удаление…', 'Removing…') : t(lang, 'Удалить', 'Remove')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export function MdrCard({ id, lang, onClose, onOpenDoc, onOpenRegister }: { id: string; lang: Lang; onClose: () => void; onOpenDoc?: (no: string) => void; onOpenRegister?: () => void }) {
  const mdrQuery = useItem<Mdr>('mdrs', id)
  const docsQuery = useList<Doc>('docs', { page: 0, pageSize: 200, filters: { mdr: [id] }, sort: [{ id: 'no', desc: false }] })
  const discLabels = useDictLabels('disc')
  /* Название и образец маски — из справочника кодирования: в реестре лежит её
     код, а человеку нужны слова. */
  const docMasks = useCoding().data?.docs ?? []
  /* Объект реестра приходит не полем, а титулом: объект стоит у титула. */
  const titulRows = useDictRows('titul')
  const [removing, setRemoving] = useState(false)
  /* Правка карточки реестра. Команда на сервере есть давно, а нажать её было
     негде: наименование, ответственный, дисциплина, стадия и объект правились
     только через базу. */
  const [editing, setEditing] = useState(false)
  /* История согласований и предлагаемые изменения. Согласованный состав нигде
     не копируется: сервер читает строки на сессию согласования и сравнивает с
     рабочими — карточка показывает результат этого сравнения. */
  const issuesQuery = useSub<RegisterIssue[]>(`/mdrs/${encodeURIComponent(id)}/issues`)
  const diffQuery = useSub<RegisterDiff>(`/mdrs/${encodeURIComponent(id)}/diff`)

  const m = mdrQuery.data
  if (!m) return (
    <Drawer open onClose={onClose} title={null}>
      <CardState lang={lang} error={mdrQuery.isError} />
    </Drawer>
  )
  const docs = docsQuery.data?.rows ?? []
  // Счётчик — total выборки, а не длина страницы: в реестре строк больше, чем
  // показывает карточка.
  const docsTotal = docsQuery.data?.total ?? 0
  const mask = docMasks.find((d) => d.code === m.docMask)
  /* Образец из справочника кодирования описывает маску ВООБЩЕ, а стадию и
     объект номер берёт у реестра: у реестра стадии AB номера будут AB-…, и
     общий образец обещал бы другое. Объект — тот, что стоит у титула реестра:
     отдельным полем он не приходит, а титул приходит. Правило то же, что у
     сервера (DocumentNumber.shape) и у диалога создания реестра. */
  const maskArea = titulRows.find((r) => r.code === m.titul)?.area ?? ''
  const shape = (mask?.segments ?? []).map((seg) => {
    if (seg.srcKind === 'seq') return '#'.repeat(seg.width && seg.width > 0 ? seg.width : 0) || '#…'
    if (seg.srcKind === 'const') return seg.value ?? seg.example
    if (seg.srcKind === 'stage') return m.stage || seg.example
    if (seg.srcKind === 'area') return maskArea || seg.example
    if (seg.srcKind === 'disc') return t(lang, '<дисц>', '<disc>')
    if (seg.srcKind === 'type') return t(lang, '<тип>', '<type>')
    return seg.example
  }).filter((part) => part !== '').join(mask?.sep ?? '-')
  const maskTitle = !m.docMask
    ? '—'
    : mask
      ? mask.title + (shape ? ' · ' + shape : '')
      : m.docMask
  // Пустой реестр — обычное дело сразу после создания; деление на ноль давало NaN%.
  const pct = m.docs ? Math.round(m.afd / m.docs * 100) : 0
  return (
    <Drawer open onClose={onClose} title={null}>
      <CardHead>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
          {/* Реестр на контракт дисциплины не имеет — пустой ярлык читался бы
              как поломка, а не как «дисциплина у каждой строки своя». */}
          {m.disc ? <DiscTag code={m.disc} name={discLabels[m.disc]} /> : null}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-subtle)' }}>{m.id}</span>
        </div>
        <div style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{m.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}><Avatar name={m.owner} size={18} />{t(lang, 'Ответственный:', 'Owner:')} {m.owner}</span>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={14} />} style={{ marginLeft: 'auto' }}
                  onClick={() => setEditing(true)}>{t(lang, 'Изменить', 'Edit')}</Button>
          <Button variant="primary" size="sm" iconLeft={<Icon name="folder-open" size={14} />} onClick={() => onOpenRegister && onOpenRegister()}>{t(lang, 'Открыть реестр', 'Open register')}</Button>
          <Button variant="danger" size="sm" iconLeft={<Icon name="trash-2" size={14} />} onClick={() => setRemoving(true)}>{t(lang, 'Удалить', 'Remove')}</Button>
        </div>
      </CardHead>
      <div style={{ flex: 1, overflow: 'auto', padding: 20, background: 'var(--surface-page)', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <KPIStat icon="files" label={t(lang, 'Документов', 'Documents')} value={m.docs} accent />
          <KPIStat icon="check-check" label={t(lang, 'Готовность AFD', 'AFD complete')} value={pct + '%'} delta={'AFD ' + m.afd} tone="up" />
          <KPIStat icon="circle-alert" label={t(lang, 'Просрочено', 'Overdue')} value={m.overdue} tone={m.overdue ? 'down' : 'neutral'} />
        </div>
        {/* Чем заведён реестр: контракт, его подрядчик, стадия и маска номера.
            Всё это решает судьбу каждой строки — номер собирается маской и
            стадией, сторона документов берётся у подрядчика, сроки проверяются
            по контракту, — а карточка показывала только счётчики, и посмотреть
            это можно было лишь в администрировании. */}
        <Panel title={t(lang, 'Сведения о реестре', 'Register details')}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, padding: '14px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
            <Field label={t(lang, 'Контракт', 'Contract')} mono>{m.contract || t(lang, '— без контракта —', '— no contract —')}</Field>
            {/* Реестр без контракта — своя документация заказчика: подрядчика у
                него нет, и прочерк здесь означает именно это. */}
            <Field label={t(lang, 'Контрагент', 'Contractor')}>
              {m.vendor
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Avatar name={m.vendor} size={18} />{m.vendor}</span>
                : t(lang, 'документация заказчика', 'owner documentation')}
            </Field>
            <Field label={t(lang, 'Стадия', 'Stage')} mono>{m.stage || '—'}</Field>
            {/* Объект реестра, а не титул: титул — свойство СТРОКИ (V90), и у
                реестра он был выведенным по объекту, то есть догадкой. */}
            <Field label={t(lang, 'Объект проекта', 'Project area')} mono>{m.area || '—'}</Field>
            {/* Маска номера документа — то, что выбрали при создании реестра:
                по ней нумеруются все его строки, а посмотреть выбранную было
                негде. Рядом — образец номера, собранный этой же маской. */}
            <Field label={t(lang, 'Маска номера документа', 'Document number mask')} mono>
              {maskTitle}
            </Field>
            {/* Состояние — подписью, а не кодом: «rework» адресовано коду.
                Словарь тот же, что у ярлыка в списке реестров. */}
            <Field label={t(lang, 'Состояние', 'Status')}>
              {m.status ? t(lang, STATUS_LABEL[m.status][0], STATUS_LABEL[m.status][1]) : '—'}
            </Field>
          </div>
        </Panel>
        <Panel title={t(lang, 'Комплектность по назначениям', 'Completion by purpose')}>
          <div style={{ padding: '14px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
            <CompletionBar afd={m.afd} ifa={m.ifa} ifr={m.ifr} planned={m.planned} height={10} />
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              <span style={{ color: 'var(--green-600)' }}>● AFD {m.afd}</span><span style={{ color: 'var(--blue-600)' }}>● IFA {m.ifa}</span>
              <span style={{ color: 'var(--accent-ink)' }}>● IFR {m.ifr}</span><span style={{ color: 'var(--text-subtle)' }}>● План {m.planned}</span>
            </div>
          </div>
        </Panel>
        <Panel title={t(lang, 'S-кривая выпуска (план / прогноз / факт)', 'S-curve (plan / forecast / actual)')}>
          <CurvePlaceholder lang={lang} />
        </Panel>
        <RegisterHistory issues={issuesQuery.data ?? []} diff={diffQuery.data} status={m.status} lang={lang} />
        <Panel title={t(lang, 'Документы реестра', 'Register documents')} count={docsTotal}>
          <DocList pending={docsQuery.isPending} error={docsQuery.isError} rows={docs.slice(0, 8)} lang={lang} onOpenDoc={onOpenDoc} />
        </Panel>
      </div>
      {editing ? <EditRegisterDialog open onClose={() => setEditing(false)} mdr={m} lang={lang} /> : null}
      <RemoveRegisterDialog open={removing} onClose={() => setRemoving(false)} kind="mdrs" code={m.id}
                            docs={docsTotal} lang={lang} onDone={() => { setRemoving(false); onClose() }} />
    </Drawer>)

}

/** Согласование реестра: когда согласовывали и что предлагается поменять. */
interface RegisterIssue { rev: number; trm: string | null; by: string | null; on: string | null }
interface RegisterDiffRow { no: string; title: string; plan: string | null; wasPlan?: string }
interface RegisterDiff {
  approved: number | null
  added: RegisterDiffRow[]
  removed: RegisterDiffRow[]
  moved: RegisterDiffRow[]
}

function RegisterHistory({ issues, diff, status, lang }: {
  issues: RegisterIssue[]
  diff?: RegisterDiff
  status?: string
  lang: Lang
}) {
  const changes = (diff?.added.length ?? 0) + (diff?.removed.length ?? 0) + (diff?.moved.length ?? 0)
  if (!issues.length && !changes) return null
  const line = (row: RegisterDiffRow, kind: 'added' | 'removed' | 'moved') => (
    <div key={kind + row.no} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0' }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-strong)' }}>{row.no}</span>
      <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.title}</span>
      <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: kind === 'removed' ? 'var(--danger-ink)' : 'var(--text-muted)' }}>
        {kind === 'moved' ? row.wasPlan + ' → ' + row.plan : row.plan ?? '—'}
      </span>
    </div>
  )
  return (
    <Panel title={t(lang, 'Согласование реестра', 'Register approval')}>
      <div style={{ padding: '12px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', display: 'grid', gap: 12 }}>
        {issues.length ? (
          <div>
            <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>
              {t(lang, 'Согласования', 'Approvals')}
            </div>
            {issues.map((i) => (
              <div key={i.rev} style={{ display: 'flex', gap: 10, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', padding: '3px 0' }}>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{t(lang, 'ред. ', 'rev ')}{i.rev}</span>
                <span style={{ color: 'var(--text-muted)' }}>{i.on ?? '—'}</span>
                {i.trm ? <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-link)' }}>{i.trm}</span> : null}
              </div>
            ))}
          </div>
        ) : null}
        {changes ? (
          <div>
            <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>
              {status === 'amending'
                ? t(lang, 'Предлагаемые изменения к согласованному', 'Proposed changes to the approved version')
                : t(lang, 'Расхождение с согласованным', 'Difference from the approved version')}
            </div>
            {(diff?.added ?? []).map((r) => line(r, 'added'))}
            {(diff?.removed ?? []).map((r) => line(r, 'removed'))}
            {(diff?.moved ?? []).map((r) => line(r, 'moved'))}
          </div>
        ) : null}
      </div>
    </Panel>
  )
}

// ---- VDR card ----------------------------------------------------------
export function VdrCard({ id, lang, onClose, onOpenDoc, onOpenRegister }: { id: string; lang: Lang; onClose: () => void; onOpenDoc?: (no: string) => void; onOpenRegister?: () => void }) {
  const vdrQuery = useItem<Vdr>('vdrs', id)
  const docsQuery = useList<Vdoc>('vdocs', { page: 0, pageSize: 200, filters: { vdr: [id] }, sort: [{ id: 'no', desc: false }] })
  const discLabels = useDictLabels('disc')
  const [removing, setRemoving] = useState(false)

  const v = vdrQuery.data
  if (!v) return (
    <Drawer open onClose={onClose} title={null}>
      <CardState lang={lang} error={vdrQuery.isError} />
    </Drawer>
  )
  const docs = docsQuery.data?.rows ?? []
  const docsTotal = docsQuery.data?.total ?? 0
  const pct = v.docs ? Math.round(v.afd / v.docs * 100) : 0
  return (
    <Drawer open onClose={onClose} title={null}>
      <CardHead>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
          <span style={{ display: 'inline-grid', placeItems: 'center', width: 26, height: 26, borderRadius: 'var(--radius-sm)', background: 'var(--violet-50)', color: 'var(--violet-600)', border: '1px solid var(--violet-100)' }}><Icon name="package" size={15} /></span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 600, color: 'var(--text-strong)' }}>{v.po}</span>
          <Badge tone="accent" size="sm">VDR</Badge>
          <DiscTag code={v.disc} />
        </div>
        <div style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{v.subject}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}><Avatar name={v.vendor} size={18} />{v.vendor}</span>
          <Button variant="primary" size="sm" iconLeft={<Icon name="folder-open" size={14} />} style={{ marginLeft: 'auto' }} onClick={() => onOpenRegister && onOpenRegister()}>{t(lang, 'Открыть реестр', 'Open register')}</Button>
          <Button variant="danger" size="sm" iconLeft={<Icon name="trash-2" size={14} />} onClick={() => setRemoving(true)}>{t(lang, 'Удалить', 'Remove')}</Button>
        </div>
      </CardHead>
      <div style={{ flex: 1, overflow: 'auto', padding: 20, background: 'var(--surface-page)', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <KPIStat icon="files" label={t(lang, 'Документов', 'Documents')} value={v.docs} accent />
          <KPIStat icon="check-check" label={t(lang, 'Готовность AFD', 'AFD complete')} value={pct + '%'} delta={'AFD ' + v.afd} tone="up" />
          <KPIStat icon="circle-alert" label={t(lang, 'Просрочено', 'Overdue')} value={v.overdue} tone={v.overdue ? 'down' : 'neutral'} />
        </div>
        <Panel title={t(lang, 'Сведения о заказе на поставку', 'Purchase order details')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, padding: '14px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
            <Field label={t(lang, 'Номер PO', 'PO number')} mono>{v.po}</Field>
            <Field label={t(lang, 'Поставщик', 'Vendor')}>{v.vendor}</Field>
            <Field label={t(lang, 'Дисциплина / пакет', 'Discipline')}><DiscTag code={v.disc} name={discLabels[v.disc]} /></Field>
            <Field label={t(lang, 'Срок выпуска', 'Due')} mono>{v.due}</Field>
            <Field label={t(lang, 'Просрочено', 'Overdue')}><span style={{ color: v.overdue ? 'var(--danger-ink)' : 'var(--text-strong)', fontFamily: 'var(--font-mono)' }}>{v.overdue}</span></Field>
          </div>
        </Panel>
        <Panel title={t(lang, 'Комплектность по назначениям', 'Completion by purpose')}>
          <div style={{ padding: '14px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
            <CompletionBar afd={v.afd} ifa={v.ifa} ifr={v.ifr} height={10} />
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              <span style={{ color: 'var(--green-600)' }}>● AFD {v.afd}</span><span style={{ color: 'var(--blue-600)' }}>● IFA {v.ifa}</span><span style={{ color: 'var(--accent-ink)' }}>● IFR {v.ifr}</span>
            </div>
          </div>
        </Panel>
        <Panel title={t(lang, 'S-кривая выпуска (план / прогноз / факт)', 'S-curve')}>
          <CurvePlaceholder lang={lang} />
        </Panel>
        <Panel title={t(lang, 'Документы поставщика', 'Vendor documents')} count={docsTotal}>
          <DocList pending={docsQuery.isPending} error={docsQuery.isError} rows={docs} lang={lang} onOpenDoc={onOpenDoc} />
        </Panel>
      </div>
      <RemoveRegisterDialog open={removing} onClose={() => setRemoving(false)} kind="vdrs" code={v.id}
                            docs={docsTotal} lang={lang} onDone={() => { setRemoving(false); onClose() }} />
    </Drawer>)

}
